import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { finalize, map, Subject, takeUntil } from 'rxjs';
import { type OutflowTypes } from 'src/app/models/additional-outflows.model';
import {
  UnidadMedida,
  type FoodItemModel,
} from 'src/app/models/food-item.model';
import { AdditionalOutflowsService } from 'src/app/services/api/additional-outflows.service';
import { AyBService } from 'src/app/services/api/ayb.service';
import { NotificationService } from 'src/shared/notification.service';
import { GroupedFoodItemOutflow } from '../models';

interface SelectedFoodItem extends GroupedFoodItemOutflow {
  observation: string;
  quantity: number;
  maxStock: number;
}

@Component({
  selector: 'app-dialog-add-additional-outflow',
  templateUrl: './dialog-create-additional-outflow.component.html',
  styleUrls: ['./dialog-create-additional-outflow.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DialogCreateAdditionalOutflowComponent
  implements OnInit, OnDestroy
{
  private _unsubscribeOnDestroy$: Subject<boolean> = new Subject<boolean>();

  actionType: 'CREATE_ADDITIONAL_OUTFLOW';
  isLoading: boolean = false;
  isLoadingWarehouse = true;

  additionalOutflowForm;
  warehouseFoodItems: FoodItemModel[] = [];
  selectedFoodItemsDataSource: SelectedFoodItem[] = [];
  dataSource: MatTableDataSource<FoodItemModel & { checked: boolean }> =
    new MatTableDataSource();

  displayedColumns: string[] = [
    'checkbox',
    'nombre-insumo',
    'stock',
    'unidad-medida',
    'peso',
    // 'precio-referencial',
  ];

  paginator!: MatPaginator;
  sort!: MatSort;

  @ViewChild(MatPaginator) set matPaginator(mp: MatPaginator) {
    this.paginator = mp;
    this.dataSource.paginator = this.paginator;
  }
  @ViewChild(MatSort) set matSort(ms: MatSort) {
    this.sort = ms;
    this.dataSource.sort = this.sort;
  }

  constructor(
    private readonly cdRef: ChangeDetectorRef,
    private readonly additionalOutflowsService: AdditionalOutflowsService,
    private readonly notificationService: NotificationService,
    private readonly alimentosService: AyBService,
    private fb: FormBuilder,
    public addAdditionalOutflowDialogRef: MatDialogRef<DialogCreateAdditionalOutflowComponent>,
    @Inject(MAT_DIALOG_DATA)
    public dialogInput: {
      actionType: 'CREATE_ADDITIONAL_OUTFLOW';
    },
  ) {
    const username = window.localStorage.getItem('email') ?? 'N/A';
    this.actionType = dialogInput.actionType;
    this.additionalOutflowForm = this.fb.group({
      observation: new FormControl('', [Validators.maxLength(255)]),
      outflowDate: new FormControl(new Date(), Validators.required),
      outflowType: new FormControl<OutflowTypes | null>(null, [
        Validators.required,
        Validators.pattern(
          /^(SALIDA POR CONSUMO|SALIDA POR VENCIMIENTO|SALIDA POR DETERIORO|SALIDA POR OTRO MOTIVO)$/,
        ),
      ]),
      username: new FormControl<string>(username, [
        Validators.email,
        Validators.required,
      ]),
      selectedFoodItems: new FormControl<SelectedFoodItem[]>(
        [],
        [Validators.required, Validators.minLength(1)],
      ),
    });
  }

  ngOnInit(): void {
    this.alimentosService
      .queryGetAllFoodItemsSku()
      .pipe(
        takeUntil(this._unsubscribeOnDestroy$),
        map((res) => res.getAllFoodItemsSku?.insumos || []),
        finalize(() => {
          this.isLoadingWarehouse = false;
          this.cdRef.markForCheck();
        }),
      )
      .subscribe({
        next: (foodItems: FoodItemModel[]) => {
          const foodItemsWithChecked = foodItems.map((item: FoodItemModel) => ({
            ...item,
            checked: false,
          }));

          this.warehouseFoodItems = foodItemsWithChecked;
          this.dataSource.data = foodItemsWithChecked;
        },
        error: (err) => {
          console.error('Error fetching food items:', err);
          this.notificationService.notifyError(
            'Error al cargar los insumos del almacén, por favor intente nuevamente.',
          );
        },
      });
  }

  ngOnDestroy(): void {
    this._unsubscribeOnDestroy$.next(true);
    this._unsubscribeOnDestroy$.complete();
  }

  applyFilter(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

  addFoodItemsToOutflow(): void {
    this.isLoading = true;
  }

  createAdditionalOutflow(): void {
    if (
      this.additionalOutflowForm.invalid ||
      this.selectedFoodItemsDataSource.length === 0 ||
      !this.areAllObservationsValid()
    ) {
      this.markFormAsTouched();
      if (this.areAllObservationsValid()) {
        this.notificationService.notifyError(
          'Por favor complete todos los campos requeridos y seleccione al menos un insumo.',
        );
      } else {
        this.notificationService.notifyError(
          'Por favor complete las observaciones para todos los insumos seleccionados.',
        );
      }
      return;
    }

    this.isLoading = true;

    this.additionalOutflowsService
      .mutationCreateAdditionalOutflow({
        observation: this.additionalOutflowForm.value.observation ?? '',
        outflowDate:
          this.additionalOutflowForm.value.outflowDate?.toISOString() ?? '',
        outflowType: this.additionalOutflowForm.value
          .outflowType as OutflowTypes,
        username: this.additionalOutflowForm.value.username ?? '',
      })
      .pipe(
        takeUntil(this._unsubscribeOnDestroy$),
        map((res) => res.createAdditionalOutflow),
        finalize(() => {
          this.isLoading = false;
          this.cdRef.markForCheck();
        }),
      )
      .subscribe({
        next: (newAdditionalOutflow) => {
          this.notificationService.showSuccessAlert(
            'Egreso adicional creado correctamente.',
          );

          this.selectedFoodItemsDataSource.forEach((foodItem) => {
            this.alimentosService
              .mutationAddFoodItemToAdditionalOutflow({
                additionalOutflowId: newAdditionalOutflow.id || '',
                foodItemId: foodItem.id || '',
                quantity: foodItem.quantity,
                observation: foodItem.observation.trim(),
              })
              .pipe(takeUntil(this._unsubscribeOnDestroy$))
              .subscribe({
                next: (res) => {
                  if (
                    res.addFoodItemToAdditionalOutflow.id &&
                    foodItem.observation.trim()
                  ) {
                    this.alimentosService
                      .mutationUpdateEgresoInsumoObservation({
                        id: res.addFoodItemToAdditionalOutflow.id,
                        observation: foodItem.observation.trim(),
                      })
                      .pipe(takeUntil(this._unsubscribeOnDestroy$))
                      .subscribe({
                        next: (observationRes) => {},
                        error: (err) => {
                          console.error('Error updating observation:', err);
                        },
                      });
                  }
                },
                error: (err) => {
                  console.error('Error adding food item to outflow:', err);
                },
              });
          });

          this.addAdditionalOutflowDialogRef.close({
            success: true,
            createdOutflow: newAdditionalOutflow,
          });
        },
        error: (err: any) => {
          console.error('Error creating additional outflow:', err);
          this.notificationService.notifyError(
            'Error al crear el egreso adicional, de persistir, por favor reportar el problema.',
          );
        },
      });
  }

  updateCheck(foodItem: GroupedFoodItemOutflow): void {
    foodItem.checked = !foodItem.checked;

    if (foodItem.checked) {
      const selectedItem: SelectedFoodItem = {
        ...foodItem,
        observation: '',
        quantity: foodItem.unidadMedida === UnidadMedida.Un ? 1 : 0.1,
        maxStock: foodItem.stock || 0,
      };
      this.selectedFoodItemsDataSource = [
        ...this.selectedFoodItemsDataSource,
        selectedItem,
      ];
    } else {
      this.selectedFoodItemsDataSource =
        this.selectedFoodItemsDataSource.filter(
          (selectedFoodItem) => selectedFoodItem.id !== foodItem.id,
        );
    }

    this.additionalOutflowForm.patchValue({
      selectedFoodItems: this.selectedFoodItemsDataSource,
    });
  }

  trackByFoodItemId(index: number, item: SelectedFoodItem): string {
    return item.id || index.toString();
  }

  removeSelectedItem(itemToRemove: SelectedFoodItem): void {
    const foodItemInDataSource = this.dataSource.data.find(
      (item) => item.id === itemToRemove.id,
    );
    if (foodItemInDataSource) {
      foodItemInDataSource.checked = false;
    }

    this.selectedFoodItemsDataSource = this.selectedFoodItemsDataSource.filter(
      (item: SelectedFoodItem) => item.id !== itemToRemove.id,
    );
    this.additionalOutflowForm.patchValue({
      selectedFoodItems: this.selectedFoodItemsDataSource,
    });
  }

  clearAllSelectedItems(): void {
    this.dataSource.data.forEach((item) => {
      item.checked = false;
    });
    this.selectedFoodItemsDataSource = [];
    this.additionalOutflowForm.patchValue({
      selectedFoodItems: this.selectedFoodItemsDataSource,
    });
  }

  markFormAsTouched(): void {
    this.additionalOutflowForm.get('selectedFoodItems')?.markAsTouched();
    this.additionalOutflowForm.markAllAsTouched();
  }

  updateQuantity(selectedFoodItem: SelectedFoodItem, event: Event): void {
    const target = event.target as HTMLInputElement;
    const newQuantity = parseFloat(target.value);

    if (
      !isNaN(newQuantity) &&
      newQuantity >= 0.1 &&
      newQuantity <= selectedFoodItem.maxStock
    ) {
      selectedFoodItem.quantity = parseFloat(newQuantity.toFixed(1));
      this._updateFormWithSelectedItems();
    } else {
      target.value = selectedFoodItem.quantity.toString();
    }
  }

  validateQuantity(item: SelectedFoodItem, event: Event): void {
    const target = event.target as HTMLInputElement;
    const newQuantity = parseFloat(target.value);

    if (isNaN(newQuantity) || newQuantity < 0.1) {
      item.quantity = 0.1;
      target.value = '0.1';
    } else if (newQuantity > item.maxStock) {
      item.quantity = item.maxStock;
      target.value = item.maxStock.toString();
    } else {
      item.quantity = parseFloat(newQuantity.toFixed(1));
      target.value = item.quantity.toString();
    }

    this._updateFormWithSelectedItems();
  }

  updateObservation(selectedFoodItem: SelectedFoodItem, event: Event): void {
    const target = event.target as HTMLInputElement;
    const newObservation = target.value.trim();

    selectedFoodItem.observation = newObservation;
    this._updateFormWithSelectedItems();
  }

  areAllObservationsValid(): boolean {
    return this.selectedFoodItemsDataSource.every(
      (item) => item.observation && item.observation.trim() !== '',
    );
  }

  getTotalSelectedQuantity(): number {
    return parseFloat(
      this.selectedFoodItemsDataSource
        .reduce((total, item) => total + item.quantity, 0)
        .toFixed(1),
    );
  }

  private _updateFormWithSelectedItems(): void {
    this.additionalOutflowForm.patchValue({
      selectedFoodItems: this.selectedFoodItemsDataSource,
    });
  }
}
